import React, { forwardRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Typography,
  Button,
  Box,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import { adminModulesConfigForRole } from '../../util/constants';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const getColorByAction = (action) => {
  const a = String(action).toLowerCase();
  if (a === 'list' || a.includes('view') || a === 'read') return 'info';
  if (a === 'create' || a.includes('add')) return 'primary';
  if (a === 'edit' || a.includes('update')) return 'warning';
  if (a === 'delete' || a.includes('remove')) return 'error';
  return 'secondary';
};

const PermissionShowDialog = ({ open, onClose, role }) => {
  const handleClose = () => {
    onClose();
  };

  const validKeys = new Set(adminModulesConfigForRole.uniqueModules.map((m) => m.key));
  const permissions = (role?.permissions || []).filter((p) => validKeys.has(p.module));
  const roleName = role?.name || 'Role';

  return (
    <Dialog
      open={open}
      keepMounted
      scroll="paper"
      onClose={handleClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          overflow: 'visible',
          width: '600px',
          maxWidth: '95vw',
          backgroundColor: '#000000',
          color: '#343a40',
        },
      }}
    >
      <DialogTitle sx={{ pr: 8, color: '#343a40' }}>
        <Typography variant="h5" component="span" sx={{ color: '#343a40' }}>
          {roleName} Permissions
        </Typography>
        <Typography variant="body1" component="div" className="mt-2" sx={{ color: '#343a40' }}>
          This role has access to {permissions.length} module
          {permissions.length === 1 ? '' : 's'} with various permission levels.
        </Typography>
        <Tooltip title="Close">
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#FFD700' }}
            size="small"
          >
            <Cancel />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          pt: 2,
          color: '#343a40',
          borderColor: '#f1f3f5',
        }}
      >
        <Stack spacing={2}>
          {permissions.length === 0 ? (
            <Typography sx={{ color: '#343a40' }}>
              No permissions assigned.
            </Typography>
          ) : (
            permissions.map((permission, index) => (
              <Paper
                key={`${permission.module}-${index}`}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#e9ecef',
                  borderColor: '#f1f3f5',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#343a40' }}>
                    {permission.module}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${(permission.actions || []).length} action(s)`}
                    variant="outlined"
                    sx={{
                      color: '#ada6f2',
                      borderColor: '#ada6f2',
                      '& .MuiChip-label': { color: '#ada6f2' },
                    }}
                  />
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {(permission.actions || []).map((action, idx) => {
                    const chipColor = getColorByAction(action);
                    const colorMap = {
                      primary: '#86c1ed',
                      info: '#86c1ed',
                      warning: '#f5af47',
                      error: '#FFD700',
                      secondary: '#343a40',
                    };
                    const chipColorVal = colorMap[chipColor] || '#343a40';
                    return (
                      <Chip
                        key={`${permission.module}-${action}-${idx}`}
                        label={action}
                        variant="outlined"
                        size="small"
                        sx={{
                          textTransform: 'capitalize',
                          color: chipColorVal,
                          borderColor: chipColorVal,
                          '& .MuiChip-label': { color: chipColorVal },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ pt: 2, borderTop: '1px solid #f1f3f5' }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            backgroundColor: '#FFD700',
            '&:hover': { backgroundColor: '#d9457a' },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionShowDialog;
